import { Box, Center, Flex, HStack, Icon, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { InternalLink } from "components/Link";
import { Text12, Text14 } from "components/Text";
import { templatePathByTemplateOrSummary } from "helpers/url/pages/v2/actionPaths";
import CDS, { ActionTypeTag } from "design-system";
import { Clock as ClockIcon } from "design-system/icons";
import { PathId } from "features/Path/models";
import { RandomEmptyActionState } from "components/EmptyComponents/RandomEmptyState";
import FilterButton from "components/Button/FilterButton";
import SearchBar from "components/SearchBar";
import ControlledDataTable, { ControlledDataTableProps } from "components/Table/ControlledDataTable";
import AddTemplateToPathButton from "features/Path/Components/Button/AddTemplateToPathButton";
import { useState, useMemo } from "react";
import { useSharedTemplatesByPrebuiltPathIdQuery } from "features/Prebuilt/PrebuiltSharedTemplate/hooks";
import { PrebuiltPathId } from "features/Prebuilt/PrebuiltPath/models";
import { PrebuiltTemplateSummary } from "features/Prebuilt/PrebuiltSharedTemplate/models";
import { prebuiltTemplatePathByTemplateSummary } from "helpers/url/pages/v2/prebuiltActionPaths";
import { ActionType } from "features/Resources/Shared/models";
import { TemplateSummary } from "../models";
import { DisplayTemplateRecipients } from "./DisplayTemplateRecipients";
import { filterTemplateSummaryByText, sortTemplatesByTiming, templateTimingDescriptor } from "../helpers";
import { useSharedTemplatesByPathIdQuery } from "../hooks";
import { TemplateTableButtonsWithRemoveFromPath, PrebuiltTemplateTableButtons } from "./TemplateTableButtons";

const columnHelper = createColumnHelper<TemplateSummary | PrebuiltTemplateSummary>();

type ScheduleTypeFilter = "all" | "task_template" | "email_template" | "meeting_template" | "message_template" | "checkpoint_template";

const TYPE_CHIPS: Array<{ value: ScheduleTypeFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "task_template", label: "Tasks" },
  { value: "email_template", label: "Emails" },
  { value: "message_template", label: "Messages" },
  { value: "meeting_template", label: "Meetings" },
  { value: "checkpoint_template", label: "Checkpoints" },
];

const buildColumns = (pathId: PathId, isPrebuilt: boolean) => [
  columnHelper.accessor((row) => row, {
    id: "templateType",
    header: "",
    cell: (row) => {
      const summary = row.getValue();
      const actionType = summary.templateType.replace(/_template$/, "") as ActionType;
      const meetingType = summary.templateType === "meeting_template" ? summary.template.meetingType : undefined;
      return (
        <ActionTypeTag.IconBadgeWithMeetingType
          actionType={actionType}
          meetingType={meetingType}
          size="sm"
        />
      );
    },
    size: 48,
    minSize: 48,
    maxSize: 48,
    enableSorting: false,
    meta: {
      sx: {
        width: "48px",
        minWidth: "48px",
        maxWidth: "48px",
        paddingLeft: 3,
        paddingRight: 0,
        overflowX: "hidden",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    cell: (row) => (
      <InternalLink
        to={
          isPrebuilt
            ? prebuiltTemplatePathByTemplateSummary(row.getValue())
            : templatePathByTemplateOrSummary(row.getValue())
        }
        textDecoration="none"
        _hover={{ textDecoration: "none" }}
      >
        <Text14 fontWeight="medium" noOfLines={1}>
          {row.getValue()?.template?.name}
        </Text14>
      </InternalLink>
    ),
    enableSorting: false,
    meta: {
      headerHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "when",
    header: "When",
    enableSorting: false,
    cell: (row) => {
      const descriptor = templateTimingDescriptor(row.getValue() as TemplateSummary);
      if (!descriptor) return null;
      return (
        <HStack spacing={1.5}>
          <Icon as={ClockIcon} boxSize={3.5} color="content.tertiary" />
          <Text12 color="content.secondary">{descriptor}</Text12>
        </HStack>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "recipients",
    header: "For",
    cell: (row) => <DisplayTemplateRecipients template={row.getValue()} />,
    enableSorting: false,
  }),

  ...(isPrebuilt
    ? [
        columnHelper.accessor((row) => row, {
          id: "buttons",
          header: "",
          cell: (row) => <PrebuiltTemplateTableButtons templateSummary={row.getValue()} />,
        }),
      ]
    : [
        columnHelper.accessor((row) => row, {
          id: "buttons",
          header: "",
          cell: (row) => <TemplateTableButtonsWithRemoveFromPath pathId={pathId} templateSummary={row.getValue()} />,
        }),
      ]),
];

interface EmptyScheduleStateProps {
  pathId: PathId;
}

function EmptyScheduleState({ pathId }: EmptyScheduleStateProps) {
  return (
    <Flex direction="column" align="center" justify="center" py="10" px="4" gap="3">
      <CDS.Text14 color="gray.700" fontWeight="500">
        No steps yet
      </CDS.Text14>
      <CDS.Text12 color="gray.500" textAlign="center" maxW="380px">
        Add tasks, emails, messages, meetings, or checkpoints to get started.
      </CDS.Text12>
      <Box mt="2">
        <AddTemplateToPathButton menuButtonProps={{ height: "32px" }} pathId={pathId} />
      </Box>
    </Flex>
  );
}

function FilteredEmptyState() {
  return (
    <Center py="10">
      <CDS.Text12 color="gray.500">No items match your filters.</CDS.Text12>
    </Center>
  );
}

interface ListTemplatesByPathIdProps {
  pathId: PathId;
}

export default function ListTemplatesByPathId({ pathId }: ListTemplatesByPathIdProps) {
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<ScheduleTypeFilter>("all");

  const { data: templates, isFetching } = useSharedTemplatesByPathIdQuery(pathId);
  const sortedTemplates = useMemo(() => sortTemplatesByTiming(templates), [templates]);

  const filteredTemplates = useMemo(() => {
    const byType = typeFilter === "all"
      ? sortedTemplates
      : sortedTemplates.filter((t) => t.templateType === typeFilter);
    return filterTemplateSummaryByText(byType, searchText);
  }, [searchText, sortedTemplates, typeFilter]);

  const hasAnyTemplates = (sortedTemplates?.length ?? 0) > 0;
  const hasActiveFilter = typeFilter !== "all" || searchText.length > 0;

  if (isFetching) {
    return (
      <Center mt="4">
        <Text14 color="fg.emphasized">Loading...</Text14>
      </Center>
    );
  }

  // True empty state — no templates at all on this path.
  if (!hasAnyTemplates) {
    return <EmptyScheduleState pathId={pathId} />;
  }

  const tableProps: ControlledDataTableProps<TemplateSummary> = {
    data: filteredTemplates || [],
    isLoading: isFetching,
    columns: buildColumns(pathId, false),
    initialPagination: { pageIndex: 0, pageSize: 100 },
    textWrap: true,
    emptyStateComponent: hasActiveFilter ? <FilteredEmptyState /> : <RandomEmptyActionState />,
  };

  return (
    <Flex direction="column">
      <Stack spacing="3" mt="2">
        <SearchBar
          border="1px solid var(--stroke-vibes, #F1F1F1)"
          minWidth="200px"
          flexShrink="1"
          mr="2"
          name="actionsSearch"
          placeholder="Search schedule"
          onInput={setSearchText}
        />
        <Flex gap="2" align="center" wrap="wrap">
          {TYPE_CHIPS.map((chip) => (
            <FilterButton
              key={chip.value}
              isActive={typeFilter === chip.value}
              onClick={() => setTypeFilter(chip.value)}
              borderRadius="full"
            >
              {chip.label}
            </FilterButton>
          ))}
        </Flex>
      </Stack>
      <Box overflowX="auto" width="100%" mt="2">
        <ControlledDataTable<TemplateSummary> {...tableProps} mt="4" />
      </Box>
    </Flex>
  );
}

interface ListPrebuiltTemplatesByPathIdProps {
  prebuiltPathId: PrebuiltPathId;
}

export function ListPrebuiltTemplatesByPathId({ prebuiltPathId }: ListPrebuiltTemplatesByPathIdProps) {
  const { data: templates, isFetching } = useSharedTemplatesByPrebuiltPathIdQuery(prebuiltPathId);
  const sortedTemplates = useMemo(() => sortTemplatesByTiming(templates), [templates]);

  if (isFetching) {
    return (
      <Center mt="4">
        <Text14 color="fg.emphasized">Loading...</Text14>
      </Center>
    );
  }

  const tableProps: ControlledDataTableProps<TemplateSummary> = {
    data: sortedTemplates || [],
    isLoading: isFetching,
    columns: buildColumns(prebuiltPathId, true),
    initialPagination: { pageIndex: 0, pageSize: 100 },
    textWrap: true,
    emptyStateComponent: <RandomEmptyActionState />,
  };

  return (
    <Flex direction="column">
      <Box overflowX="auto" width="100%" mt="2">
        <ControlledDataTable<TemplateSummary> {...tableProps} mt="4" />
      </Box>
    </Flex>
  );
}
